import { apiRequest } from "./apiService";

const encId = (id) => encodeURIComponent(String(id == null ? "" : id));

const normalizeText = (value) => String(value == null ? "" : value).trim();

const INSTAGRAM_USERNAME_RE = /^[A-Za-z0-9._]{1,30}$/;
const RESERVED_INSTAGRAM_PATHS = new Set([
  "about",
  "accounts",
  "developer",
  "direct",
  "directory",
  "explore",
  "p",
  "reel",
  "reels",
  "stories",
]);

/**
 * Accept a public Instagram @handle or profile URL and return only the username.
 * Reject other websites and Instagram content URLs so an invalid profile cannot
 * consume an advisor credit or be presented as verified creator context.
 */
export const normalizeInstagramProfileInput = (value) => {
  let input = normalizeText(value);
  if (!input) return "";

  input = input.replace(/^@/, "").trim();

  const looksLikeUrl = /^(?:https?:\/\/|www\.|instagram\.com\/)/i.test(input);
  if (looksLikeUrl) {
    try {
      const parsed = new URL(
        /^https?:\/\//i.test(input) ? input : `https://${input}`
      );
      const host = parsed.hostname.toLowerCase().replace(/^www\./, "");
      if (host !== "instagram.com") return "";

      const segments = parsed.pathname.split("/").filter(Boolean);
      if (segments.length !== 1) return "";
      input = segments[0];
    } catch {
      return "";
    }
  }

  if (
    RESERVED_INSTAGRAM_PATHS.has(input.toLowerCase()) ||
    !INSTAGRAM_USERNAME_RE.test(input)
  ) {
    return "";
  }

  return input;
};

const normalizePlatform = (value) =>
  normalizeText(value || "instagram").toLowerCase() || "instagram";

const requireObjectPayload = (payload, label) => {
  if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
    throw new Error(`Valid payload is required for ${label}.`);
  }
};

const requireRequestId = (requestId) => {
  if (requestId == null || String(requestId).trim() === "") {
    throw new Error("requestId is required.");
  }
};

const normalizeStringArray = (value) => {
  if (Array.isArray(value)) {
    return value.map(normalizeText).filter(Boolean);
  }

  if (typeof value === "string") {
    return value
      .split("\n")
      .map(normalizeText)
      .filter(Boolean);
  }

  return [];
};

const withOptional = (target, key, value) => {
  const clean = typeof value === "string" ? normalizeText(value) : value;

  if (clean !== undefined && clean !== null && clean !== "") {
    target[key] = clean;
  }

  return target;
};

/**
 * POST /creator-collaboration-advisor/evaluate
 *
 * Customer self-service evaluation:
 * creator profile/handle + creator DM + business context
 * → fast first-pass risk score, recommendation, extracted details, suggested replies.
 *
 * Deeper AI review is processed asynchronously by the backend worker.
 */
export const evaluateCreatorCollaborationAdvisor = (payload) => {
  requireObjectPayload(payload, "creator collaboration advisor evaluation");

  const businessId = normalizeText(payload.businessId);
  const businessType = normalizeText(payload.businessType);
  const city = normalizeText(payload.city);
  const campaignGoal = normalizeText(payload.campaignGoal);
  const creatorMessage = normalizeText(payload.creatorMessage);

  const influencerId = normalizeText(payload.influencerId);
  const creatorHandle = normalizeInstagramProfileInput(payload.creatorHandle);

  if (!businessId) {
    throw new Error("businessId is required.");
  }

  if (!businessType) {
    throw new Error("businessType is required.");
  }

  if (!city) {
    throw new Error("city is required.");
  }

  if (!campaignGoal) {
    throw new Error("campaignGoal is required.");
  }

  if (!creatorMessage) {
    throw new Error("creatorMessage is required.");
  }

  if (!influencerId && !creatorHandle) {
    throw new Error("Enter a valid public Instagram handle or profile URL.");
  }

  const proposedDeliverables = normalizeStringArray(payload.proposedDeliverables);

  const body = {
    businessId,
    businessType,
    city,
    campaignGoal,
    creatorMessage,
    platform: normalizePlatform(payload.platform),
  };

  withOptional(body, "businessName", payload.businessName);
  withOptional(
    body,
    "countryCode",
    normalizeText(payload.countryCode || "GB").toUpperCase()
  );
  withOptional(body, "influencerId", influencerId);
  withOptional(body, "creatorId", payload.creatorId);
  withOptional(body, "creatorHandle", creatorHandle);
  withOptional(
    body,
    "creatorProfileUrl",
    creatorHandle ? `https://www.instagram.com/${creatorHandle}/` : ""
  );
  withOptional(body, "requestedGift", payload.requestedGift);

  if (
    payload.requestedFee !== undefined &&
    payload.requestedFee !== null &&
    payload.requestedFee !== ""
  ) {
    body.requestedFee = Number(payload.requestedFee);
  }

  if (proposedDeliverables.length > 0) {
    body.proposedDeliverables = proposedDeliverables;
  }

  return apiRequest("/creator-collaboration-advisor/evaluate", "POST", body);
};

/**
 * GET /creator-collaboration-advisor/{requestId}/status
 *
 * Reads stored async advisor status/result.
 *
 * Important:
 * - Does not run AI again.
 * - Does not consume another advisor usage credit.
 * - Used by frontend Refresh / polling after evaluate has returned first-pass result.
 */
export const getCreatorCollaborationAdvisorStatus = (requestId) => {
  requireRequestId(requestId);

  return apiRequest(
    `/creator-collaboration-advisor/${encId(requestId)}/status`,
    "GET"
  );
};

/**
 * POST /creator-collaboration-advisor/{requestId}/accept-and-create-campaign
 *
 * Customer accepts the advisor recommendation:
 * business decision → campaign + offer/code + invite/tracking assets.
 */
export const acceptCreatorCollaborationAndCreateCampaign = (
  requestId,
  payload
) => {
  requireRequestId(requestId);
  requireObjectPayload(payload, "creator collaboration campaign acceptance");

  const businessDecision = normalizeText(payload.businessDecision);

  if (!businessDecision) {
    throw new Error("businessDecision is required.");
  }

  const body = {
    businessDecision,
  };

  withOptional(body, "decisionNote", payload.decisionNote);
  withOptional(body, "campaignName", payload.campaignName);
  withOptional(body, "campaignGoal", payload.campaignGoal);
  withOptional(body, "offerTitle", payload.offerTitle);
  withOptional(
    body,
    "discountType",
    normalizeText(payload.discountType || "PERCENT").toUpperCase()
  );
  withOptional(
    body,
    "currency",
    normalizeText(payload.currency || "GBP").toUpperCase()
  );
  withOptional(body, "landingUrl", payload.landingUrl);

  if (
    payload.discountValue !== undefined &&
    payload.discountValue !== null &&
    payload.discountValue !== ""
  ) {
    body.discountValue = Number(payload.discountValue);
  }

  return apiRequest(
    `/creator-collaboration-advisor/${encId(
      requestId
    )}/accept-and-create-campaign`,
    "POST",
    body
  );
};
